import { MenuItem } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { enqueueSnackbar } from "notistack"
import { useMemo, useState } from "react"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import {
    deleteProductGroup,
    fetchProductGroups,
    FetchProductGroupsResponse,
    ProductGroupQuery,
} from "../productGroupApi"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { useNavigate } from "react-router-dom"
import {
    getEditProductGroupRoute,
    MERCHANT_ADMIN_PRODUCTS_GROUP_CREATE_ROUTE,
    MERCHANT_ADMIN_ROUTE,
} from "../../../../../constants/routes"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { ProductGroupItem } from "../productGroupTypes"
import { useMerchant } from "../../../../merchants/merchantHooks"

function transformData(data: FetchProductGroupsResponse) {
    return data.productGroups
}

type ProductGroupPageProps = Readonly<{}>

export function ProductGroupPage({}: ProductGroupPageProps) {
    const { l10n } = useAppLocalization()

    const { merchantId } = useMerchant()
    const navigate = useNavigate()

    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [confirmModalText, setConfirmModalText] = useState("")
    const [productGroupId, setProductGroupId] = useState<number>()

    const productGroups = useApiQuery(fetchProductGroups, {
        queryName: ProductGroupQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(productGroups)

    const columns = useMemo<MRT_ColumnDef<ProductGroupItem>[]>(
        () => [
            columnFactory.shortNameColumn({
                header: l10n.getString("product-group-code-column-header"),
                id: "productGroupCode",
                accessorFn: (row) => row.productGroupCode?.number ?? "",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("product-group-name-column-header"),
                accessorKey: "name",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("product-group-status-column-header"),
                },
                (row) => row.isActive,
            ),
        ],
        [columnFactory, l10n],
    )

    const { mutateAsync: callDeleteProductGroup } = useApiMutation(deleteProductGroup, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ProductGroupQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    return (
        <>
            <LocalizedStrict id="product-groups-page-header-title" attrs={{ title: true }}>
                <PageHeader
                    title="Product groups"
                    page={Page.MERCHANT_PRODUCT_GROUP}
                    rightElement={
                        <LocalizedStrict id="product-groups-create-button">
                            <StandardButton onClick={() => navigate(MERCHANT_ADMIN_PRODUCTS_GROUP_CREATE_ROUTE)}>
                                Create new
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-product-groups">
                            <Breadcrumb>Product groups</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                enableStickyHeader
                columns={columns}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <MenuItem
                        key={`edit-${row.original.id}`}
                        onClick={() => {
                            navigate(getEditProductGroupRoute(row.original.id))
                            closeMenu()
                        }}
                    >
                        {l10n.getString("edit")}
                    </MenuItem>,
                    <MenuItem
                        key={`delete-${row.original.id}`}
                        onClick={() => {
                            setShowConfirmModal(true)
                            setConfirmModalText(row.original.name)
                            setProductGroupId(row.original.id)
                            closeMenu()
                        }}
                    >
                        {l10n.getString("delete")}
                    </MenuItem>,
                ]}
            />

            {showConfirmModal && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("product-group-delete")}
                    entityName={confirmModalText}
                    handleClick={() => {
                        if (productGroupId) {
                            callDeleteProductGroup({ productGroupId, merchantId: merchantId ?? 0 })
                        } else {
                            throw new Error("Product group id is undefined")
                        }
                    }}
                    onClose={() => setShowConfirmModal(false)}
                    open={showConfirmModal}
                />
            )}
        </>
    )
}
